import { FileText, Package, Puzzle } from "lucide-react";
import { type SyntheticEvent, useState } from "react";
import { RecordWindow } from "../../web/components/RecordWindow";
import { ErrorNotice, Notice } from "../../web/components/ui";
import { useNewUuid } from "../../web/lib/newUuid";
import { itemName, usePriceList, usePriceListSaver } from "./data";
import { archivedMatches } from "./archived";
import { ITEM_NUMBER_MAX, type MaterialRow } from "./meta";

/**
 * Adding a material: a name (and item number), then it opens in its own window to fill in the rest. `kind`: an item (in ServiceM8:
 * plain, or built from products, its products added next: packages.ts), or a product in the Catalogue (catalogue.ts: its item number
 * optional, only items reach the accounting software). `onCreated`: with whether it's built from products. One archived with that name
 * or item number is offered back instead (archivedMatches).
 */
export function NewItemWindow(props: Readonly<{ kind: "item" | "product"; onCreated: (uuid: string, built: boolean) => void; onClose: () => void }>) {
    const saver = usePriceListSaver();
    // Its uuid, the same for every try (a retry after a timeout can't make a second).
    const newUuid = useNewUuid();
    const { data } = usePriceList();
    const product = props.kind === "product";
    const [name, setName] = useState("");
    const [itemNumber, setItemNumber] = useState("");
    // Built from products: its price and cost theirs (packages.ts).
    const [built, setBuilt] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const ready = name.trim() !== "" && (product || itemNumber.trim() !== "");
    const archived = data ? archivedMatches(data.materials, product, name, itemNumber).slice(0, 3) : [];
    const word = product ? "product" : "item";
    // Back for sale as it was (its pricing, products and history kept), opened as a new one would be.
    const restore = (m: MaterialRow) => {
        setBusy(true);
        setError(null);
        saver
            .setItemState(m.uuid, "sale")
            .then(() => props.onCreated(m.uuid, false))
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };

    const submit = (e: SyntheticEvent) => {
        e.preventDefault();
        if (!ready) return;
        setBusy(true);
        setError(null);
        const fields = { name: name.trim(), item_number: itemNumber.trim() };
        let made;
        if (product) made = saver.createMaterial({ ...fields, product: true, uuid: newUuid.get() });
        else if (built) made = saver.makePackage({ create: fields }, []);
        else made = saver.createMaterial({ ...fields, uuid: newUuid.get() });
        made
            .then((m) => {
                newUuid.made();
                props.onCreated(m.uuid, !product && built);
            })
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };

    return (
        <RecordWindow
            kind={props.kind}
            tone={product ? "rose" : undefined}
            icon={product ? <Puzzle className="size-4" /> : <Package className="size-4" />}
            title={product ? "New product" : "New item"}
            tabs={[{ id: "details", label: "Details", icon: <FileText className="size-5" /> }]}
            tab="details"
            onTab={() => undefined}
            onClose={props.onClose}
        >
            <form className="mx-auto w-full max-w-lg space-y-4" onSubmit={submit}>
                <label className="block space-y-1">
                    <span className="text-xs font-medium text-muted">Name</span>
                    <input className="input text-sm" value={name} onChange={(e) => setName(e.target.value)} required />
                </label>
                <label className="block space-y-1">
                    <span className="text-xs font-medium text-muted">Item number{product ? " (optional)" : ""}</span>
                    <input className="input text-sm" value={itemNumber} maxLength={ITEM_NUMBER_MAX} onChange={(e) => setItemNumber(e.target.value)} required={!product} />
                    {/* An item's is required: accounting software matches invoice lines to its items by it (rules.ts assertItemNumberGiven). */}
                    <span className="block text-xs text-muted">
                        {product ? "Your own code for it, e.g. the supplier's. Only here: it can be an item's too." : "Unique to this item. Accounting software matches invoice lines to items by it."}
                    </span>
                </label>
                {product ? null : (
                    <label className="flex items-start gap-2 text-sm">
                        <input type="checkbox" className="mt-0.5 size-4" checked={built} onChange={(e) => setBuilt(e.target.checked)} />
                        <span>
                            <span className="block">Linked to Catalogue</span>
                            <span className="block text-xs text-muted">Its price and cost are what its products in the Catalogue add up to</span>
                        </span>
                    </label>
                )}
                {archived.map((m) => (
                    <Notice
                        key={m.uuid}
                        title={`There's an archived ${word} like it.`}
                        actions={
                            <button type="button" className="btn-secondary" disabled={busy} onClick={() => restore(m)}>
                                Restore it instead
                            </button>
                        }
                    >
                        {data ? itemName(data, m) : m.name}
                        {m.item_number ? ` · ${m.item_number}` : ""}. Restoring it keeps its sales history together; a new {word} starts again.
                    </Notice>
                ))}
                <ErrorNotice error={error} />
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-secondary" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={busy || !ready}>
                        {product ? "Add product" : "Add item"}
                    </button>
                </div>
                <p className="text-xs text-muted">
                    {(() => {
                        if (product) return "Set its pricing, cost and supplier prices next. Items sold in ServiceM8 are linked to it.";
                        return built ? "Add its products next: its price is what they add up to." : "Set its price and cost next.";
                    })()}
                </p>
            </form>
        </RecordWindow>
    );
}
